import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { RoleService } from '../services/role.service';
import { ApiService } from '../services/api.service';
import { NavigationService } from '../services/navigation.service';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {
  constructor(
    private roleService: RoleService,
    private apiService: ApiService,
    private navigationService: NavigationService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    if (state.url.includes('/signup') || currentPath.includes('/signup')) {
      return true;
    }

    console.log('RoleGuard canActivate called for route:', state.url);
    
    const currentRole = this.roleService.getCurrentRole();
    const currentUser = this.apiService.getCurrentUser();

    // Check if user is authenticated
    if (!currentUser) {
      console.log('RoleGuard: No current user, redirecting to login');
      this.router.navigate(['/login']);
      return false;
    }

    // Define allowed roles for team management - be more flexible
    const allowedRoles = ['admin', 'vendor', 'super admin', 'manager', 'org admin', 'organization admin', 'operator', 'Admin', 'Vendor', 'Super Admin', 'Manager'];
    
    // Check multiple ways the user might have access
    let hasAccess = false;
    
    // 1. Check current role from role service
    if (currentRole && allowedRoles.some(role => 
      currentRole.toLowerCase() === role.toLowerCase()
    )) {
      hasAccess = true;
    }
    
    // 2. Check user type
    if (currentUser.type && allowedRoles.some(role => 
      currentUser.type!.toLowerCase() === role.toLowerCase()
    )) {
      hasAccess = true;
    }
    
    // 3. Check if user is system admin
    if (currentUser.email === 'system_admin@kormoan.com') {
      hasAccess = true;
    }
    
    // 4. Check normalized role
    const normalizedRole = this.roleService.normalizeRole({
      roleId: currentUser.role_id,
      roleName: currentUser.role?.name || currentRole,
      type: currentUser.type,
      email: currentUser.email
    }).toLowerCase();
    if (allowedRoles.some(r => r.toLowerCase() === normalizedRole)) {
      hasAccess = true;
    }

    // 5. Check if user has permissions or is authenticated
    const perms = currentUser.permissions || currentUser.role?.permission;
    if (perms && Array.isArray(perms) && perms.length > 0) {
      hasAccess = true;
    } else if (localStorage.getItem('token') && (currentUser.type || currentUser.role_id)) {
      hasAccess = true;
    }
    
    if (!hasAccess) {
      console.log('RoleGuard: Access denied - insufficient role');
      console.log('Current role:', currentRole);
      console.log('User type:', currentUser.type);
      console.log('User permissions:', currentUser.role?.permission);
      
      // Instead of redirecting to dashboard, show access denied or redirect to login
      this.router.navigate(['/login']);
      return false;
    }

    console.log('RoleGuard: Access granted');
    return true;
  }
}
